import { useState, useRef, useEffect, useCallback, memo } from 'react';
import clsx from 'clsx';
import { SVGIcon } from '../SVGIcon';
import { BadgeStatusType } from '../Badge/BadgeTypes';
import { TireCard } from './TireCard';
import { TiresControlMobile } from './TiresControlMobile';
import type { TiresControlProps, TirePosition } from './TiresControl.types';

import leftCar from './left.png';
import rightCar from './right.png';
import { Badge } from '../Badge/Badge';

// Stałe dla pozycji znaczników opon (w pikselach przy szerokości 660px)
const MAX_CONTAINER_WIDTH = 660;
const TIRE_POSITIONS = {
  frontLeft: 100,    // left position
  rearLeft: 475,    // left position
  frontRight: 101,   // right position
  rearRight: 475,   // right position
  bottom: 50,
} as const;

// Stałe dla rozmiarów elementów przy max szerokości
const BASE_CIRCLE_SIZE = 78;
const BASE_LINE_HEIGHT = 51;

const POSITION_LABELS: Record<TirePosition, string> = {
  'front-left': 'Lewy przód',
  'front-right': 'Prawy przód',
  'rear-left': 'Lewy tył',
  'rear-right': 'Prawy tył',
};

interface TireVisualizationProps {
  isSelected: boolean;
  onSelect: () => void;
  label: string;
  className?: string;
  style?: React.CSSProperties;
  scale?: number;
}

const TireVisualization = memo(({
  isSelected,
  onSelect,
  label,
  className,
  style,
  scale = 1,
}: TireVisualizationProps) => {
  const circleSize = Math.round(BASE_CIRCLE_SIZE * scale);
  const lineHeight = Math.round(BASE_LINE_HEIGHT * scale);
  const dotSize = Math.round(6 * scale);

  return (
    <div className={clsx('flex flex-col gap-3 items-center', className)} style={style}>
      <div className="relative">
        <svg
          width={circleSize}
          height={circleSize}
          viewBox="0 0 74 74"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="37" cy="37" r="36" stroke="#DADDEB" strokeWidth="2" strokeDasharray="2 2" />
        </svg>

        <button
          onClick={onSelect}
          className={clsx(
            'absolute top-1/2 left-1/2 rounded-full transition-all -translate-x-1/2 -translate-y-1/2 hover:scale-101',
            isSelected
              ? ' bg-[#0064FF]/30 border-2 border-primary '
              : ' hover:bg-[#0064FF]/30  transition-all',
          )}
          style={{ width: `${circleSize}px`, height: `${circleSize}px` }}
          aria-label={label}
        />
      </div>
      {
        <div className="flex absolute flex-col items-center" style={{ bottom: `-${lineHeight + 2}px` }}>
          <div className={clsx('w-[2px]', isSelected ? 'bg-primary' : 'bg-gray-400')} style={{ height: `${lineHeight}px` }} />
          <div
            className={clsx('rounded-full', isSelected ? 'bg-primary' : 'bg-gray-400')}
            style={{ width: `${dotSize}px`, height: `${dotSize}px` }}
          />
        </div>
      }
    </div>
  );
});

export const TiresControl = ({
  tires,
  hasSecondSet = false,
  expertSummary,
  onTireSelect,
  selectedPosition: controlledSelectedPosition,
}: TiresControlProps) => {
  const [internalSelectedPosition, setInternalSelectedPosition] =
    useState<TirePosition | null>(null);
  const [containerWidth, setContainerWidth] = useState(MAX_CONTAINER_WIDTH);
  const leftContainerRef = useRef<HTMLDivElement>(null);

  // Obserwuj zmiany szerokości kontenera
  useEffect(() => {
    const container = leftContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        setContainerWidth(Math.min(width, MAX_CONTAINER_WIDTH));
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // Oblicz dynamiczne pozycje znaczników
  const calculatePosition = (basePosition: number) => {
    // Dla szerokości >= 1600px używamy stałych wartości
    if (containerWidth >= MAX_CONTAINER_WIDTH) {
      return basePosition;
    }
    // Dla mniejszych szerokości skalujemy proporcjonalnie
    return (basePosition / MAX_CONTAINER_WIDTH) * containerWidth;
  };

  const tirePositions = {
    frontLeft: calculatePosition(TIRE_POSITIONS.frontLeft),
    rearLeft: calculatePosition(TIRE_POSITIONS.rearLeft),
    frontRight: calculatePosition(TIRE_POSITIONS.frontRight),
    rearRight: calculatePosition(TIRE_POSITIONS.rearRight),
    bottom: calculatePosition(TIRE_POSITIONS.bottom),
  };

  // Oblicz scale dla elementów (okręgi, linie)
  const scale = containerWidth / MAX_CONTAINER_WIDTH;

  const selectedPosition =
    controlledSelectedPosition ?? internalSelectedPosition;

  const handleTireClick = useCallback((position: TirePosition) => {
    if (onTireSelect) {
      onTireSelect(position);
    }
    setInternalSelectedPosition(position);
  }, [onTireSelect]);

  const getTireByPosition = (position: TirePosition) => {
    return tires.find((tire) => tire.position === position);
  };

  const frontLeftTire = getTireByPosition('front-left');
  const frontRightTire = getTireByPosition('front-right');
  const rearLeftTire = getTireByPosition('rear-left');
  const rearRightTire = getTireByPosition('rear-right');

  return (
    <>
      {/* Wersja mobilna - poniżej lg */}
      <div className="lg:hidden">
        <TiresControlMobile
          tires={tires}
          hasSecondSet={hasSecondSet}
          expertSummary={expertSummary}
          onTireSelect={onTireSelect}
          selectedPosition={controlledSelectedPosition}
        />
      </div>

      {/* Wersja desktop - od lg w górę */}
      <div className="hidden w-full bg-white border lg:block rounded-card border-grayBorder">
        {/* Wizualizacja samochodu z góry */}
        <div className="px-5">
          <div className="flex">
            {/* Lewy przód */}
            <div ref={leftContainerRef} className="relative flex-1 pt-10 pb-0 border-r border-grayBorder max-w-[660px]">
              <img src={leftCar} className="w-full" />
              <h3 className="absolute top-4 left-0 font-inter text-[12px] font-normal leading-[14px] text-text-primary">
                {POSITION_LABELS['front-left']}
              </h3>
              <h3 className="absolute top-4 right-5 font-inter text-[12px] font-normal leading-[14px] text-text-primary">
                {POSITION_LABELS['rear-left']}
              </h3>
              {frontLeftTire && (
                <TireVisualization
                  className="absolute"
                  style={{ bottom: `${tirePositions.bottom}px`, left: `${tirePositions.frontLeft}px` }}
                  isSelected={selectedPosition === 'front-left'}
                  onSelect={() => handleTireClick('front-left')}
                  label={POSITION_LABELS['front-left']}
                  scale={scale}
                />
              )}

              {/* Lewy tył */}
              {rearLeftTire && (
                <TireVisualization
                  className="absolute"
                  style={{ bottom: `${tirePositions.bottom}px`, left: `${tirePositions.rearLeft}px` }}
                  isSelected={selectedPosition === 'rear-left'}
                  onSelect={() => handleTireClick('rear-left')}
                  label={POSITION_LABELS['rear-left']}
                  scale={scale}
                />
              )}
            </div>
            <div className="relative flex-1 pt-10 pb-0 max-w-[660px]">
              <img src={rightCar} className="w-full" />
              <h3 className="absolute top-4 left-5 font-inter text-[12px] font-normal leading-[14px] text-text-primary">
                {POSITION_LABELS['rear-right']}
              </h3>
              <h3 className="absolute top-4 right-0 font-inter text-[12px] font-normal leading-[14px] text-text-primary">
                {POSITION_LABELS['front-right']}
              </h3>

              {rearRightTire && (
                <TireVisualization
                  className="absolute"
                  style={{ bottom: `${tirePositions.bottom}px`, right: `${tirePositions.frontRight}px` }}
                  isSelected={selectedPosition === 'front-right'}
                  onSelect={() => handleTireClick('front-right')}
                  label={POSITION_LABELS['front-right']}
                  scale={scale}
                />
              )}

              {/* Prawy przód */}
              {frontRightTire && (
                <TireVisualization
                  className="absolute"
                  style={{ bottom: `${tirePositions.bottom}px`, right: `${tirePositions.rearRight}px` }}
                  isSelected={selectedPosition === 'rear-right'}
                  onSelect={() => handleTireClick('rear-right')}
                  label={POSITION_LABELS['rear-right']}
                  scale={scale}
                />
              )}
            </div>
          </div>
        </div>

        <div className="h-px bg-grayBorder" />

        {/* Karty opon */}
        <div className="grid grid-cols-4">
          {frontLeftTire && (
            <div
              className={clsx(
                'border-r border-grayBorder bg-primary-200 transition-all',
                selectedPosition === 'front-left' && 'border-2 border-primary',
              )}
            >
              <TireCard
                tire={frontLeftTire}
                isSelected={selectedPosition === 'front-left'}
                onClick={() => handleTireClick('front-left')}
              />
            </div>
          )}
          {rearLeftTire && (
            <div
              className={clsx(
                'border-r border-grayBorder bg-white transition-all',
                selectedPosition === 'rear-left' && 'border-2 border-primary',
              )}
            >
              <TireCard
                tire={rearLeftTire}
                isSelected={selectedPosition === 'rear-left'}
                onClick={() => handleTireClick('rear-left')}
              />
            </div>
          )}
          {rearRightTire && (
            <div
              className={clsx(
                'border-r border-grayBorder bg-primary-200 transition-all',
                selectedPosition === 'rear-right' && 'border-2 border-primary',
              )}
            >
              <TireCard
                tire={rearRightTire}
                isSelected={selectedPosition === 'rear-right'}
                onClick={() => handleTireClick('rear-right')}
              />
            </div>
          )}
          {frontRightTire && (
            <div
              className={clsx(
                'bg-white transition-all',
                selectedPosition === 'front-right' && 'border-2 border-primary',
              )}
            >
              <TireCard
                tire={frontRightTire}
                isSelected={selectedPosition === 'front-right'}
                onClick={() => handleTireClick('front-right')}
              />
            </div>
          )}
        </div>

        <div className="h-px bg-grayBorder" />

        {/* Pytanie o drugi komplet */}
        <div className="flex items-center gap-3 px-[17px] py-3">
          <p className="font-inter text-[14px] font-semibold leading-4 text-text-primary">
            Czy sprzedający posiada drugi komplet opon?
          </p>
          <Badge
            variant={hasSecondSet ? BadgeStatusType.Success : BadgeStatusType.Error}
            version='v1'
            size='sm'
            badgeText={hasSecondSet ? 'TAK' : 'NIE'}
          />
        </div>

        {/* Podsumowanie rzeczoznawcy */}
        {expertSummary && (
          <div className="p-5 bg-gradient-to-b from-gray-100 to-white rounded-b-card">
            <div className="flex gap-3 items-start">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-primary">
                <SVGIcon name="wheel" size={20} fill="white" />
              </div>
              <div>
                <h4 className="mb-2 text-lg font-bold leading-6 font-archivo text-text-primary">
                  Podsumowanie Rzeczoznawcy dotyczące kontroli Ogumienia
                </h4>
                <p className="font-inter text-[15px] font-normal leading-6 text-text-muted">
                  {expertSummary}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
